// @ts-nocheck
/**
 * ContentsPropertiesModal - FHNW Customization
 *
 * Extended with batch processing for Relations fields:
 * - universities (Hochschulen)
 * - organisation_entities (Organisationseinheit)
 * - topics (Themen)
 * - research_areas (Forschungsfelder)
 *
 * Supports two operations:
 * - ADD: Values are APPENDED to existing values
 * - REMOVE: Values are REMOVED from existing values
 *
 * Since Relations fields are not in the Catalog index, we fetch
 * the current values directly from the REST API before updating.
 *
 * Enhanced with:
 * - Loading spinner during save operation
 * - Progress bar showing batch processing status
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useIntl } from 'react-intl';
import { contentsPropertiesModalMessages } from '@fhnw/messages/contentsPropertiesModal';
import { toast } from 'react-toastify';
import { Progress } from 'semantic-ui-react';
import { createBaseSchema } from './ContentsPropertiesModalSchema';
import { cloneDeepSchema } from '@plone/volto/helpers/Utils/Utils';
import { ModalForm } from '@plone/volto/components/manage/Form';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import config from '@plone/volto/registry';
import Api from '@plone/volto/helpers/Api/Api';
import { nestContent } from '@plone/volto/helpers/Content/Content';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { searchContent } from '@plone/volto/actions/search/search';

/**
 * Mapping of batch ADD fields to their target fields
 * Key: form field name (with _to_add suffix)
 * Value: actual content field name
 */
const BATCH_FIELDS_ADD_MAPPING = {
  universities_to_add: 'universities',
  organisation_entities_to_add: 'organisation_entities',
  topics_to_add: 'topics',
  research_areas_to_add: 'research_areas',
  future_fields_to_add: 'future_fields',
};

/**
 * Mapping of batch REMOVE fields to their target fields
 * Key: form field name (with _to_remove suffix)
 * Value: actual content field name
 */
const BATCH_FIELDS_REMOVE_MAPPING = {
  universities_to_remove: 'universities',
  organisation_entities_to_remove: 'organisation_entities',
  topics_to_remove: 'topics',
  research_areas_to_remove: 'research_areas',
  future_fields_to_remove: 'future_fields',
};

/**
 * Target fields that need to be fetched from the API
 */
const BATCH_TARGET_FIELDS = Object.values(BATCH_FIELDS_ADD_MAPPING);

/**
 * Batch fields that hold vocabulary tokens (not relation UIDs).
 * For these, extractUIDFromToken must NOT be applied — the raw token is the value.
 */
const VOCABULARY_BATCH_FIELDS = new Set(['future_fields']);

/**
 * Standard fields that use replace logic (not append)
 */
const STANDARD_FIELDS = [
  'effective',
  'expires',
  'rights',
  'creators',
  'exclude_from_nav',
  'link',
];

// Extract UID from hierarchical vocabulary token (format: [[PARENT_UID]_]*[UID])
const extractUIDFromToken = (token) => {
  if (typeof token !== 'string') return null;
  return token.replace(/^([^_]+_)*/g, '');
};

/**
 * Extract tokens/UIDs from relation field values
 * Relations can be stored as:
 * - Array of objects with token, UID, or @id property
 * - Array of tokens/UIDs (strings)
 * - null/undefined
 *
 * Note: For vocabulary-based relations (like organisation_entities),
 * the values are stored as objects with {title, token} structure.
 */
const extractTokens = (relationValue) => {
  if (!relationValue || !Array.isArray(relationValue)) {
    return [];
  }
  return relationValue
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      // Vocabulary-based relations use 'token', regular relations use 'UID' or '@id'
      return item?.token || item?.UID || item?.['@id'] || null;
    })
    .filter(Boolean);
};

/**
 * Extract choices from relation field values for use in Remove dropdowns.
 * Returns an array of [value, label] tuples that can be used as choices.
 * This ensures we show the actual stored values, not the current vocabulary.
 */
const extractChoices = (relationValue) => {
  if (!relationValue || !Array.isArray(relationValue)) {
    return [];
  }
  return relationValue
    .map((item) => {
      if (typeof item === 'string') {
        // For plain strings, use the value as both token and label
        return [item, item];
      }
      // For objects, extract token/UID and title
      const token = item?.token || item?.UID || item?.['@id'] || null;
      const title = item?.title || item?.Title || token || 'Unknown';
      if (token) {
        return [token, title];
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * Progress Bar Component - renders via Portal into the Modal actions area
 * Shows batch processing progress during save operation
 */
const BatchProgressBar = ({ current, total, visible }) => {
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    // Find the modal actions area to inject the progress bar
    if (visible) {
      const modalActions = document.querySelector(
        '.ui.modal.batch-saving .actions',
      );
      if (modalActions) {
        setPortalTarget(modalActions);
      }
    } else {
      setPortalTarget(null);
    }
  }, [visible]);

  if (!visible || total === 0 || !portalTarget) return null;

  const progressPercent = Math.round((current / total) * 100);

  // eslint-disable-next-line no-console
  // console.log('BatchProgressBar rendering:', {
  //   current,
  //   total,
  //   progressPercent,
  //   visible,
  // });

  // Styles for the progress bar container inside modal actions
  const containerStyle = {
    width: '100%',
    padding: '10px 0',
    marginBottom: '10px',
  };

  const textStyle = {
    display: 'block',
    textAlign: 'center',
    marginTop: '8px',
    fontWeight: 'bold',
    color: '#000',
    fontSize: '12px',
  };

  /**
   * Use createPortal to render inside the modal actions
   */
  return createPortal(
    <div className="batch-progress-container" style={containerStyle}>
      <Progress
        percent={progressPercent}
        indicating
        progress
        size="small"
        className="batch-progress-bar"
      />
      <span className="batch-progress-text" style={textStyle}>
        {current} / {total}
      </span>
    </div>,
    portalTarget,
  );
};

const ContentsPropertiesModal = (props) => {
  const { onCancel, onOk, open, items, values } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token);
  const pathname = useSelector((state) => state.router?.location?.pathname);

  /**
   * State to store fetched relation values for each item
   * tokens: { itemUrl: { field: [token1, token2, ...] } }
   * choices: { itemUrl: { field: [[token, title], ...] } }
   */
  const [fetchedRelations, setFetchedRelations] = useState({});
  const [fetchedChoices, setFetchedChoices] = useState({});
  // Token → human-readable title for `future_fields` (Choice/Set, not a relation),
  // populated from `fhnw.vocabularies.future_field` so Remove-dropdown shows labels.
  const [futureFieldLabels, setFutureFieldLabels] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  /**
   * Ref to track if component is mounted (for async operations)
   */
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch relation values when modal opens
   * We use both the catalog values (from props) and fetch from API for complete data
   */
  useEffect(() => {
    if (open && items?.length > 0) {
      const fetchRelations = async () => {
        setIsLoading(true);
        const api = new Api();
        const relations = {};
        const choices = {};

        try {
          // Load the future_fields vocabulary in parallel so the Remove
          // dropdown can show human-readable labels instead of raw tokens.
          const vocabPromise = api
            .get('/@vocabularies/fhnw.vocabularies.future_field?b_size=10000', {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((resp) => {
              const map = {};
              for (const term of resp?.items || []) {
                if (term?.token) {
                  map[term.token] = term.title || term.token;
                }
              }
              return map;
            })
            .catch(() => ({}));

          // Fetch each item's data to get current relation values
          const [vocabMap] = await Promise.all([
            vocabPromise,
            ...items.map(async (itemUrl, idx) => {
              // First, try to get values from the catalog (passed via props)
              const catalogValues = values?.[idx] || {};

              try {
                const response = await api.get(itemUrl, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                });

                relations[itemUrl] = {};
                choices[itemUrl] = {};
                for (const field of BATCH_TARGET_FIELDS) {
                  // Use API response if available, otherwise fall back to catalog values
                  const apiValue = response[field];
                  const catalogValue = catalogValues[field];
                  const valueToUse =
                    apiValue !== undefined ? apiValue : catalogValue;
                  relations[itemUrl][field] = extractTokens(valueToUse);
                  choices[itemUrl][field] = extractChoices(valueToUse);
                }
              } catch {
                // Fall back to catalog values on error
                relations[itemUrl] = {};
                choices[itemUrl] = {};
                for (const field of BATCH_TARGET_FIELDS) {
                  relations[itemUrl][field] = extractTokens(
                    catalogValues[field],
                  );
                  choices[itemUrl][field] = extractChoices(
                    catalogValues[field],
                  );
                }
              }
            }),
          ]);
          setFetchedRelations(relations);
          setFetchedChoices(choices);
          setFutureFieldLabels(vocabMap || {});
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching relations:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchRelations();
    }
  }, [open, items, token, values]);

  /**
   * Compute merged choices for Remove dropdowns.
   * Combines choices from all selected items, removing duplicates.
   * This ensures users can remove any value that exists on any selected item.
   */
  const mergedRemoveChoices = useMemo(() => {
    const result = {};
    for (const field of BATCH_TARGET_FIELDS) {
      const choicesMap = new Map();
      for (const itemUrl of Object.keys(fetchedChoices)) {
        const itemChoices = fetchedChoices[itemUrl]?.[field] || [];
        for (const [tokenValue, title] of itemChoices) {
          if (!choicesMap.has(tokenValue)) {
            // `future_fields` stores raw vocabulary tokens; fall back to the
            // vocabulary title so users see e.g. "New Work" instead of "new_work".
            const label =
              field === 'future_fields'
                ? futureFieldLabels[tokenValue] || title
                : title;
            choicesMap.set(tokenValue, label);
          }
        }
      }
      // Convert Map to array of [value, label] for the widget
      result[field] = Array.from(choicesMap.entries());
    }
    return result;
  }, [fetchedChoices, futureFieldLabels]);

  const baseSchema = createBaseSchema({ intl, mergedRemoveChoices });
  const schemaEnhancer = config.settings.contentPropertiesSchemaEnhancer;

  let schema = schemaEnhancer
    ? schemaEnhancer({
        schema: cloneDeepSchema(baseSchema),
        intl,
      })
    : baseSchema;

  /**
   * Memoize initialData to avoid recalculating on every render.
   * This computes the initial form values based on common values across all selected items.
   */
  const initialData = useMemo(() => {
    /** @type {Record<string, any>} */
    const data = {};
    if (values?.length) {
      for (const name of Object.keys(schema.properties)) {
        const firstValue = values[0][name];
        // should not show floor or ceiling dates
        if (
          (name === 'effective' && firstValue && firstValue <= '1970') ||
          (name === 'expires' && firstValue && firstValue >= '2499')
        ) {
          continue;
        }
        if (values.every((item) => item[name] === firstValue)) {
          data[name] = firstValue;
        }
      }
    }
    return data;
  }, [values, schema.properties]);

  /**
   * Normalize values: ArrayWidget may return objects with {title, token/value}
   * or strings depending on the vocabulary. We need to extract the token/value.
   */
  const normalizeValue = useCallback((item) => {
    if (typeof item === 'string') {
      return extractUIDFromToken(item);
    }
    // For vocabulary-based fields, prefer 'token', then 'value', then 'UID'
    return extractUIDFromToken(
      item?.token || item?.value || item?.UID || item?.['@id'] || null,
    );
  }, []);

  /**
   * Handle form submission with SERIAL processing to avoid ZODB ConflictErrors.
   */
  const onSubmit = useCallback(
    async (data) => {
      // Check if any batch fields have values to add or remove
      const hasAddChanges = Object.keys(BATCH_FIELDS_ADD_MAPPING).some(
        (addField) => data[addField]?.length > 0,
      );
      const hasRemoveChanges = Object.keys(BATCH_FIELDS_REMOVE_MAPPING).some(
        (removeField) => data[removeField]?.length > 0,
      );
      const hasBatchChanges = hasAddChanges || hasRemoveChanges;

      // Collect standard field changes (replace logic)
      const standardChanges = {};
      for (const name of STANDARD_FIELDS) {
        if (data[name] !== undefined && data[name] !== initialData[name]) {
          standardChanges[name] = data[name];
        }
      }

      // If no changes at all, just close the modal
      if (isEmpty(standardChanges) && !hasBatchChanges) {
        onOk();
        return;
      }

      // Build changes for each selected item
      // For batch fields, we merge with existing values fetched from API
      const contentUpdates = items.map((itemUrl) => {
        const itemChanges = { ...standardChanges };

        // Process each target field for add/remove operations
        for (const targetField of BATCH_TARGET_FIELDS) {
          const addField = `${targetField}_to_add`;
          const removeField = `${targetField}_to_remove`;
          const toAddRaw = data[addField] || [];
          const toRemoveRaw = data[removeField] || [];

          // Vocabulary fields store raw tokens; relation fields need UID extraction.
          const normalize = VOCABULARY_BATCH_FIELDS.has(targetField)
            ? (item) =>
                typeof item === 'string'
                  ? item
                  : item?.token ?? item?.value ?? null
            : normalizeValue;
          const toAdd = toAddRaw.map(normalize).filter(Boolean);
          const toRemove = toRemoveRaw.map(normalize).filter(Boolean);

          // Only process if there are changes for this field
          if (toAdd.length > 0 || toRemove.length > 0) {
            // Get existing values from fetched relations (not from catalog values)
            const existingValues =
              fetchedRelations[itemUrl]?.[targetField] || [];

            // Step 1: Add new values (merge with existing, remove duplicates)
            let newValues = [...new Set([...existingValues, ...toAdd])];

            // Step 2: Remove specified values
            if (toRemove.length > 0) {
              newValues = newValues.filter((uid) => !toRemove.includes(uid));
            }

            // Only include in changes if there's an actual difference
            // This prevents sending empty arrays for items that never had values
            const hasChanged =
              newValues.length !== existingValues.length ||
              !newValues.every((v) => existingValues.includes(v));

            if (hasChanged) {
              itemChanges[targetField] = newValues;
            }
          }
        }

        return itemChanges;
      });

      // Start serial processing with progress tracking
      setIsSaving(true);
      setProgress({ current: 0, total: items.length });

      const api = new Api();
      let hasError = false;
      let errorMessage = null;

      for (let i = 0; i < items.length; i++) {
        if (!isMountedRef.current) break;

        const itemUrl = items[i];
        const itemContent = contentUpdates[i];

        // Skip if no changes for this item
        if (isEmpty(itemContent)) {
          setProgress({ current: i + 1, total: items.length });
          continue;
        }

        try {
          await api.patch(itemUrl, {
            data: nestContent(itemContent),
          });
          if (isMountedRef.current) {
            setProgress({ current: i + 1, total: items.length });
            // Small delay to ensure progress bar is visible
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (error) {
          hasError = true;
          errorMessage =
            error?.response?.body?.message ||
            error?.message ||
            intl.formatMessage(
              contentsPropertiesModalMessages.batchUpdateErrorContent,
            );
          // eslint-disable-next-line no-console
          console.error(`Failed to update ${itemUrl}:`, error);
          break;
        }
      }

      if (!isMountedRef.current) return;

      setIsSaving(false);
      setProgress({ current: 0, total: 0 });

      if (hasError) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(
              contentsPropertiesModalMessages.batchUpdateError,
            )}
            content={errorMessage}
          />,
        );
      } else {
        // Refresh the contents table by dispatching searchContent
        if (pathname) {
          dispatch(
            searchContent(getBaseUrl(pathname), {
              'path.depth': 1,
              sort_on: 'getObjPositionInParent',
              metadata_fields: '_all',
              b_size: 50,
            }),
          );
        }
        onOk();
      }
    },
    [
      items,
      initialData,
      fetchedRelations,
      normalizeValue,
      intl,
      onOk,
      dispatch,
      pathname,
    ],
  );

  return (
    <>
      {open && (
        <ModalForm
          open={open}
          onSubmit={onSubmit}
          onCancel={onCancel}
          title={intl.formatMessage(contentsPropertiesModalMessages.properties)}
          schema={schema}
          formData={initialData}
          loading={isLoading || isSaving}
          className={isSaving ? 'batch-saving' : ''}
        />
      )}
      <BatchProgressBar
        current={progress.current}
        total={progress.total}
        visible={isSaving && progress.total > 0}
      />
    </>
  );
};

ContentsPropertiesModal.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  open: PropTypes.bool.isRequired,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
export default ContentsPropertiesModal;
